import React, { useState, useEffect, useCallback } from "react";
import { RefreshCw, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import FindCaseStepper from "../../components/crm/FindCaseStepper";
import { Field, SelectShell, inputStyles, selectStyles } from "../../components/crm/formControls";
import {
  getNgtCaptcha,
  searchNgtCases,
  getNgtKeywords,
  getNgtSubKeywords,
} from "../../services/ngtApi";
import NGT_KEYWORD_MAP from "./ngtKeywordData";
import NGTJudgementSearch from "./NGTJudgementSearch";

import SelectMenu from "../../components/crm/SelectMenu";
// Top-level feature tabs: Case-Status search vs. Judgement/Order search.
const MAIN_TABS = [
  { id: "case-status", label: "Case Status" },
  { id: "judgement", label: "Judgement / Orders" },
];

// Party Name is temporarily unavailable upstream (NGT returns no form/captcha
// for it), so it's listed last and disabled. Keep it in the list so the form
// logic for it stays intact for when it's re-enabled.
const SEARCH_TABS = [
  { id: "case-number", label: "Case Number" },
  { id: "free-text", label: "Free Text" },
  { id: "keyword", label: "Keyword" },
  { id: "advocate", label: "Advocate" },
  { id: "party-name", label: "Party Name", disabled: true },
];

// Offline/instant fallback for the keyword dropdowns (live values are fetched
// dynamically; this is used if the API returns nothing or errors).
const fallbackKeywords = (zone) =>
  (NGT_KEYWORD_MAP[zone]?.keywords || []).map((k) => ({ value: k.value, label: k.label }));
const fallbackSubKeywords = (zone, keyword1) =>
  (NGT_KEYWORD_MAP[zone]?.keywords || []).find((k) => k.value === keyword1)?.subKeywords || [];

const STATUS_OPTIONS = [
  { value: "", label: "Select Status" },
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "disposed", label: "Disposed" },
];

const SEARCH_TIPS = [
  "Select a Zonal Bench to search",
  "Captcha is case sensitive",
  "Refresh the captcha if it's unclear",
];

function NGTSearch() {
  const navigate = useNavigate();

  // UI state
  const [mainTab, setMainTab] = useState("case-status");
  const [activeTab, setActiveTab] = useState("case-number");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Captcha + form options (zones / types) for the active tab
  const [zones, setZones] = useState([]);
  const [types, setTypes] = useState([]);
  const [captchaImage, setCaptchaImage] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [cookies, setCookies] = useState(null);

  // Year options — NGT was constituted in 2010
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: currentYear - 2009 }, (_, i) => String(currentYear - i));

  // Separate form state per tab
  const [partyForm, setPartyForm] = useState({
    zone: "",
    partyType: "",
    partyName: "",
    year: "",
    status: "",
    captcha: "",
  });
  const [caseNumberForm, setCaseNumberForm] = useState({
    zone: "",
    caseType: "",
    caseNumber: "",
    year: "",
    captcha: "",
  });
  const [freeTextForm, setFreeTextForm] = useState({
    zone: "",
    searchBy: "",
    text: "",
    captcha: "",
  });
  const [keywordForm, setKeywordForm] = useState({
    zone: "",
    keyword1: "",
    keyword2: "",
    captcha: "",
  });
  const [advocateForm, setAdvocateForm] = useState({
    zone: "",
    advocateName: "",
    year: "",
    captcha: "",
  });

  // Dynamic keyword dropdowns (zone-dependent, loaded on demand)
  const [kwKeywords, setKwKeywords] = useState([]);
  const [kwSubKeywords, setKwSubKeywords] = useState([]);
  const [kwLoading, setKwLoading] = useState(false);
  const [subKwLoading, setSubKwLoading] = useState(false);

  // Load the main keyword list (keyword1) when a zone is picked on the Keyword tab
  const handleKeywordZoneChange = async (zone) => {
    setKeywordForm((prev) => ({ ...prev, zone, keyword1: "", keyword2: "" }));
    setKwKeywords([]);
    setKwSubKeywords([]);
    if (!zone) return;
    setKwLoading(true);
    try {
      let list = await getNgtKeywords(zone);
      if (!Array.isArray(list) || list.length === 0) list = fallbackKeywords(zone);
      setKwKeywords(list);
    } catch {
      setKwKeywords(fallbackKeywords(zone));
    } finally {
      setKwLoading(false);
    }
  };

  // Load the sub-keyword list (keyword2) when a main keyword is picked
  const handleKeyword1Change = async (keyword1) => {
    setKeywordForm((prev) => ({ ...prev, keyword1, keyword2: "" }));
    setKwSubKeywords([]);
    if (!keyword1) return;
    setSubKwLoading(true);
    try {
      let list = await getNgtSubKeywords(keywordForm.zone, keyword1);
      if (!Array.isArray(list) || list.length === 0)
        list = fallbackSubKeywords(keywordForm.zone, keyword1);
      setKwSubKeywords(list);
    } catch {
      setKwSubKeywords(fallbackSubKeywords(keywordForm.zone, keyword1));
    } finally {
      setSubKwLoading(false);
    }
  };

  // Fetch captcha + form options for a given search type
  const loadCaptcha = useCallback(async (searchType) => {
    setCaptchaLoading(true);
    setError("");
    setCaptchaImage("");
    try {
      const data = await getNgtCaptcha(searchType);
      if (data?.success === false) {
        setError(data.error || "Failed to load captcha. Please try again.");
        return;
      }
      setZones(Array.isArray(data?.zones) ? data.zones : []);
      setTypes(Array.isArray(data?.types) ? data.types : []);
      setCaptchaImage(data?.captchaImage || "");
      setCookies(data?.cookies || null);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Failed to load captcha");
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  // Load captcha on mount and whenever the active tab changes
  useEffect(() => {
    loadCaptcha(activeTab);
    setError("");
    // Clear the captcha input for the tab being switched to
    switch (activeTab) {
      case "party-name":
        setPartyForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "case-number":
        setCaseNumberForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "free-text":
        setFreeTextForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "keyword":
        setKeywordForm((prev) => ({ ...prev, captcha: "" }));
        break;
      case "advocate":
        setAdvocateForm((prev) => ({ ...prev, captcha: "" }));
        break;
      default:
        break;
    }
  }, [activeTab, loadCaptcha]);

  const handleRefreshCaptcha = () => loadCaptcha(activeTab);

  // Search handler
  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      let params;
      switch (activeTab) {
        case "party-name":
          params = {
            zone_type: partyForm.zone,
            party_type: partyForm.partyType,
            party_name: partyForm.partyName,
            case_year: partyForm.year,
            status: partyForm.status || "all",
            captcha_input: partyForm.captcha,
            cookies,
          };
          break;
        case "case-number":
          params = {
            zone_type: caseNumberForm.zone,
            case_type: caseNumberForm.caseType,
            case_number: caseNumberForm.caseNumber,
            case_year: caseNumberForm.year,
            captcha_input: caseNumberForm.captcha,
            cookies,
          };
          break;
        case "free-text":
          params = {
            zone_type: freeTextForm.zone,
            search_by: freeTextForm.searchBy,
            text_name: freeTextForm.text,
            captcha_input: freeTextForm.captcha,
            cookies,
          };
          break;
        case "keyword":
          params = {
            zone_type: keywordForm.zone,
            keyword1: keywordForm.keyword1,
            keyword2: keywordForm.keyword2,
            captcha_input: keywordForm.captcha,
            cookies,
          };
          break;
        case "advocate":
          params = {
            zone_type: advocateForm.zone,
            advocate_name: advocateForm.advocateName,
            case_year: advocateForm.year,
            captcha_input: advocateForm.captcha,
            cookies,
          };
          break;
        default:
          return;
      }

      const data = await searchNgtCases(activeTab, params);

      if (data?.success === false) {
        const msg = data.error || "Search failed. Please try again.";
        setError(msg);
        toast.error(msg, { duration: 5000 });
        loadCaptcha(activeTab);
        return;
      }

      const cases = Array.isArray(data?.cases) ? data.cases : [];
      if (cases.length === 0) {
        const msg = "No cases found for the given search criteria.";
        setError(msg);
        toast.error(msg, { duration: 5000 });
        loadCaptcha(activeTab);
        return;
      }

      navigate("/portal/ngt/caseList", {
        state: {
          cases,
          searchType: activeTab,
          cookies,
          searchParams: params,
        },
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Failed to search cases";
      setError(msg);
      toast.error(msg, { duration: 5000 });
      loadCaptcha(activeTab);
    } finally {
      setLoading(false);
    }
  };

  // Reset handler
  const handleReset = () => {
    switch (activeTab) {
      case "party-name":
        setPartyForm({ zone: "", partyType: "", partyName: "", year: "", status: "", captcha: "" });
        break;
      case "case-number":
        setCaseNumberForm({ zone: "", caseType: "", caseNumber: "", year: "", captcha: "" });
        break;
      case "free-text":
        setFreeTextForm({ zone: "", searchBy: "", text: "", captcha: "" });
        break;
      case "keyword":
        setKeywordForm({ zone: "", keyword1: "", keyword2: "", captcha: "" });
        setKwKeywords([]);
        setKwSubKeywords([]);
        break;
      case "advocate":
        setAdvocateForm({ zone: "", advocateName: "", year: "", captcha: "" });
        break;
      default:
        break;
    }
    loadCaptcha(activeTab);
  };

  // Reusable Zonal Bench dropdown
  const renderZoneField = (value, onChange) => (
    <Field label="Zonal Bench">
      <SelectShell busy={captchaLoading}>
        <SelectMenu chevron={false} value={value} onChange={onChange} className={selectStyles} required>
          <option value="" disabled>
            {captchaLoading ? "Loading benches..." : "Select Bench"}
          </option>
          {zones.map((z) => (
            <option key={z.value} value={z.value}>
              {z.label}
            </option>
          ))}
        </SelectMenu>
      </SelectShell>
    </Field>
  );

  // Render tab-specific fields based on active tab
  const renderLeftFields = () => {
    switch (activeTab) {
      case "party-name":
        return (
          <>
            {renderZoneField(partyForm.zone, (e) => setPartyForm({ ...partyForm, zone: e.target.value }))}
            <Field label="Party Type">
              <SelectShell>
                <SelectMenu chevron={false}
                  value={partyForm.partyType}
                  onChange={(e) => setPartyForm({ ...partyForm, partyType: e.target.value })}
                  className={selectStyles}
                  required
                >
                  <option value="" disabled>Select Party Type</option>
                  {types.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </SelectMenu>
              </SelectShell>
            </Field>
            <Field label="Party Name">
              <input
                type="text"
                value={partyForm.partyName}
                onChange={(e) => setPartyForm({ ...partyForm, partyName: e.target.value })}
                placeholder="Party Name"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Case Year" required={false}>
              <SelectShell>
                <SelectMenu chevron={false}
                  value={partyForm.year}
                  onChange={(e) => setPartyForm({ ...partyForm, year: e.target.value })}
                  className={selectStyles}
                >
                  <option value="">Select Year</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </SelectMenu>
              </SelectShell>
            </Field>
            <Field label="Status" required={false}>
              <SelectShell>
                <SelectMenu chevron={false}
                  value={partyForm.status}
                  onChange={(e) => setPartyForm({ ...partyForm, status: e.target.value })}
                  className={selectStyles}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </SelectMenu>
              </SelectShell>
            </Field>
          </>
        );
      case "case-number":
        return (
          <>
            {renderZoneField(caseNumberForm.zone, (e) => setCaseNumberForm({ ...caseNumberForm, zone: e.target.value }))}
            <Field label="Case Type">
              <SelectShell>
                <SelectMenu chevron={false}
                  value={caseNumberForm.caseType}
                  onChange={(e) => setCaseNumberForm({ ...caseNumberForm, caseType: e.target.value })}
                  className={selectStyles}
                  required
                >
                  <option value="" disabled>Select Case Type</option>
                  {types.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </SelectMenu>
              </SelectShell>
            </Field>
            <Field label="Case Number">
              <input
                type="text"
                value={caseNumberForm.caseNumber}
                onChange={(e) => setCaseNumberForm({ ...caseNumberForm, caseNumber: e.target.value })}
                placeholder="Case Number"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Case Year" required={false}>
              <SelectShell>
                <SelectMenu chevron={false}
                  value={caseNumberForm.year}
                  onChange={(e) => setCaseNumberForm({ ...caseNumberForm, year: e.target.value })}
                  className={selectStyles}
                >
                  <option value="">Select Year</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </SelectMenu>
              </SelectShell>
            </Field>
          </>
        );
      case "free-text":
        return (
          <>
            {renderZoneField(freeTextForm.zone, (e) => setFreeTextForm({ ...freeTextForm, zone: e.target.value }))}
            <Field label="Search By">
              <SelectShell>
                <SelectMenu chevron={false}
                  value={freeTextForm.searchBy}
                  onChange={(e) => setFreeTextForm({ ...freeTextForm, searchBy: e.target.value })}
                  className={selectStyles}
                  required
                >
                  <option value="" disabled>Select</option>
                  {types.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </SelectMenu>
              </SelectShell>
            </Field>
            <Field label="Free Text">
              <input
                type="text"
                value={freeTextForm.text}
                onChange={(e) => setFreeTextForm({ ...freeTextForm, text: e.target.value })}
                placeholder="Enter search text (min 3 characters)"
                className={inputStyles}
                required
              />
            </Field>
          </>
        );
      case "keyword":
        return (
          <>
            {renderZoneField(keywordForm.zone, (e) => handleKeywordZoneChange(e.target.value))}
            <Field label="Keyword">
              <SelectShell busy={kwLoading}>
                <SelectMenu chevron={false}
                  value={keywordForm.keyword1}
                  onChange={(e) => handleKeyword1Change(e.target.value)}
                  className={selectStyles}
                  required
                  disabled={!keywordForm.zone || kwLoading}
                >
                  <option value="" disabled>
                    {!keywordForm.zone
                      ? "Select a bench first"
                      : kwLoading
                      ? "Loading keywords..."
                      : "Select Keyword"}
                  </option>
                  {kwKeywords.map((k) => (
                    <option key={k.value} value={k.value}>{k.label}</option>
                  ))}
                </SelectMenu>
              </SelectShell>
            </Field>
            <Field label="Sub Keyword">
              <SelectShell busy={subKwLoading}>
                <SelectMenu chevron={false}
                  value={keywordForm.keyword2}
                  onChange={(e) => setKeywordForm({ ...keywordForm, keyword2: e.target.value })}
                  className={selectStyles}
                  required
                  disabled={!keywordForm.keyword1 || subKwLoading}
                >
                  <option value="" disabled>
                    {!keywordForm.keyword1
                      ? "Select a keyword first"
                      : subKwLoading
                      ? "Loading sub keywords..."
                      : "Select Sub Keyword"}
                  </option>
                  {kwSubKeywords.map((k) => (
                    <option key={k.value} value={k.value}>{k.label}</option>
                  ))}
                </SelectMenu>
              </SelectShell>
            </Field>
          </>
        );
      case "advocate":
        return (
          <>
            {renderZoneField(advocateForm.zone, (e) => setAdvocateForm({ ...advocateForm, zone: e.target.value }))}
            <Field label="Advocate Name">
              <input
                type="text"
                value={advocateForm.advocateName}
                onChange={(e) => setAdvocateForm({ ...advocateForm, advocateName: e.target.value })}
                placeholder="Advocate Name"
                className={inputStyles}
                required
              />
            </Field>
            <Field label="Case Year" required={false}>
              <SelectShell>
                <SelectMenu chevron={false}
                  value={advocateForm.year}
                  onChange={(e) => setAdvocateForm({ ...advocateForm, year: e.target.value })}
                  className={selectStyles}
                >
                  <option value="">Select Year</option>
                  {yearOptions.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </SelectMenu>
              </SelectShell>
            </Field>
          </>
        );
      default:
        return null;
    }
  };

  // Get captcha value + setter for active tab
  const getCaptchaProps = () => {
    switch (activeTab) {
      case "party-name":
        return { value: partyForm.captcha, onChange: (e) => setPartyForm({ ...partyForm, captcha: e.target.value }) };
      case "case-number":
        return { value: caseNumberForm.captcha, onChange: (e) => setCaseNumberForm({ ...caseNumberForm, captcha: e.target.value }) };
      case "free-text":
        return { value: freeTextForm.captcha, onChange: (e) => setFreeTextForm({ ...freeTextForm, captcha: e.target.value }) };
      case "keyword":
        return { value: keywordForm.captcha, onChange: (e) => setKeywordForm({ ...keywordForm, captcha: e.target.value }) };
      case "advocate":
        return { value: advocateForm.captcha, onChange: (e) => setAdvocateForm({ ...advocateForm, captcha: e.target.value }) };
      default:
        return { value: "", onChange: () => {} };
    }
  };

  const activeTabLabel = SEARCH_TABS.find((t) => t.id === activeTab)?.label || "";
  const captchaProps = getCaptchaProps();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">

      {/* Full-screen overlay while the search request is in flight */}
      {loading && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl px-8 py-7 shadow-2xl text-center max-w-sm w-full mx-4">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-1">Loading…</h3>
            <p className="text-sm text-slate-500">Please wait a moment.</p>
          </div>
        </div>
      )}

        <main className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-1.5 text-sm">
            <Link to="/portal/findCasesOptions" className="text-slate-500 hover:text-slate-800">Find Cases</Link>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <span className="font-medium text-slate-800">NGT</span>
          </div>
          <div className="mt-4"><FindCaseStepper current={2} /></div>

          {/* Category toggle: Case Status vs. Judgement / Orders */}
          <div className="mt-4 flex justify-center">
            <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 p-1">
              {MAIN_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setMainTab(tab.id)}
                  className={`rounded-full px-6 py-1.5 text-sm font-semibold transition-all duration-200 ${
                    mainTab === tab.id
                      ? "bg-white text-slate-900 shadow-sm ring-1 ring-slate-200"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {mainTab === "judgement" && <NGTJudgementSearch />}

          {mainTab === "case-status" && (
            <div className="mt-4 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <form onSubmit={handleSearch}>
                <div className="p-5 sm:p-6">
                  {/* Search By pills */}
                  <p className="mb-2 text-center text-xs font-medium text-slate-600">Search By</p>
                  <div className="flex flex-wrap justify-center gap-2">
                    {SEARCH_TABS.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => !tab.disabled && setActiveTab(tab.id)}
                        disabled={tab.disabled}
                        title={tab.disabled ? "Temporarily unavailable" : undefined}
                        className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-all duration-200 ${
                          tab.disabled
                            ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400"
                            : activeTab === tab.id
                            ? "bg-yellow-500 text-slate-900 shadow-sm"
                            : "border border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <h2 className="mt-6 text-sm font-semibold text-slate-800">
                    NGT : Search by {activeTabLabel}
                  </h2>

                  {error && (
                    <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 ring-1 ring-red-100">{error}</p>
                  )}

                  {/* Fields + captcha share one grid so they align across the full width */}
                  <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4">
                    {renderLeftFields()}

                    <Field label="Captcha" className="lg:col-start-1">
                      <div className="flex items-stretch gap-2">
                        <div className="flex h-10 flex-1 items-center justify-center rounded-lg border border-slate-300 bg-slate-50">
                          {captchaLoading ? (
                            <span className="flex items-center gap-2 text-sm text-slate-500">
                              <RefreshCw className="h-4 w-4 animate-spin" />
                              Loading...
                            </span>
                          ) : captchaImage ? (
                            <img
                              src={captchaImage}
                              alt="CAPTCHA"
                              className="max-h-[34px] object-contain"
                              onError={() => setError("Failed to load captcha image.")}
                            />
                          ) : (
                            <span className="px-2 text-center text-xs text-slate-400">Captcha unavailable</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={handleRefreshCaptcha}
                          disabled={captchaLoading}
                          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 transition-colors hover:border-yellow-400 hover:text-yellow-600 disabled:opacity-50"
                          title="Refresh Captcha"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      </div>
                    </Field>

                    <Field label="Enter Captcha">
                      <input
                        type="text"
                        value={captchaProps.value}
                        onChange={captchaProps.onChange}
                        placeholder="Enter Captcha"
                        className={inputStyles}
                        required
                      />
                    </Field>
                  </div>
                </div>

                {/* Footer — tips + actions */}
                <div className="flex flex-col gap-4 border-t border-slate-100 bg-slate-50/60 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  <ul className="flex flex-wrap items-center gap-x-5 gap-y-1">
                    {SEARCH_TIPS.map((tip) => (
                      <li key={tip} className="flex items-center gap-1.5 text-xs text-slate-500">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-400" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                  <div className="flex shrink-0 gap-3">
                    <button
                      type="button"
                      onClick={handleReset}
                      className="h-10 rounded-lg border border-slate-300 bg-white px-6 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
                      disabled={loading}
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      className="h-10 rounded-lg bg-yellow-500 px-8 text-sm font-semibold text-slate-900 transition-colors hover:bg-yellow-400 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={loading || captchaLoading || !captchaImage}
                    >
                      {loading ? "Searching..." : "Search Cases"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </main>
    </div>
  );
}

export default NGTSearch;
